import { useLazyQuery } from '@apollo/client';
import { exportXlsModal } from 'motion-components';
import { gqlGetQuery } from '../graphql';
import { buildWhereFromFilterValues } from './filters';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { calculateDistance } from 'shared-pages/utils/geo';
import { regionTitle } from 'shared-pages/common/constants';

export const exportColumns = (t) => [
  { fieldName: 'id', fieldTitle: t('ID'), fieldType: 'TEXT' },
  { fieldName: 'qrCode', fieldTitle: t('QR Code'), fieldType: 'TEXT' },
  { fieldName: 'binSerialNo', fieldTitle: t('Αριθμός Κάδου'), fieldType: 'TEXT' },
  {
    fieldName: 'collectionpoint.code',
    fieldTitle: t('Κωδικός Σημείου Συλλογής'),
    fieldType: 'TEXT',
  },
  { fieldName: 'collectionpoint.name', fieldTitle: t('Σημείο Συλλογής'), fieldType: 'TEXT' },
  {
    fieldName: 'collectionpoint.addressLine1',
    fieldTitle: t('Διεύθυνση Σημείου Συλλογής'),
    fieldType: 'TEXT',
  },
  {
    fieldName: 'collectionpoint.addressArea',
    fieldTitle: t('Περιοχή Σημείου Συλλογής'),
    fieldType: 'TEXT',
  },
  {
    fieldName: 'collectionpoint.addressCity',
    fieldTitle: t('Πόλη Σημείου Συλλογής'),
    fieldType: 'TEXT',
  },
  {
    fieldName: 'collectionpoint.region.name',
    fieldTitle: t(regionTitle + ' ' + 'Σημείου Συλλογής'),
    fieldType: 'TEXT',
  },
  { fieldName: 'containertype.name', fieldTitle: t('Τύπος Συσκευασίας'), fieldType: 'TEXT' },
  { fieldName: 'declaredByUser', fieldTitle: t('Χρήστης Δήλωσης'), fieldType: 'TEXT' },
  { fieldName: 'declaredDate', fieldTitle: t('Ημ/νία Δήλωσης'), fieldType: 'DATE' },
  { fieldName: 'comments', fieldTitle: t('Σχόλια'), fieldType: 'TEXT' },
  { fieldName: 'latitude', fieldTitle: t('Γεωγραφικό πλάτος (Latitude) Κάδου'), fieldType: 'TEXT' },
  {
    fieldName: 'longitude',
    fieldTitle: t('Γεωγραφικό μήκος (Longitude) Κάδου'),
    fieldType: 'TEXT',
  },
  { fieldName: 'googlemapurl', fieldTitle: t('Google Map  Κάδου'), fieldType: 'TEXT' },
  {
    fieldName: 'collectionpoint.latitude',
    fieldTitle: t('Γεωγραφικό πλάτος (Latitude) Σημείου'),
    fieldType: 'TEXT',
  },
  {
    fieldName: 'collectionpoint.longitude',
    fieldTitle: t('Γεωγραφικό μήκος (Longitude) Σημείου'),
    fieldType: 'TEXT',
  },
  { fieldName: 'cp_googlemapurl', fieldTitle: t('Google Map Σημείου'), fieldType: 'TEXT' },
  { fieldName: 'distance', fieldTitle: t('Απόσταση Κάδου με Σημείο σε Μέτρα'), fieldType: 'TEXT' },
  { fieldName: 'isNewCollectionpoint', fieldTitle: t('Είναι Νέο Σημείο'), fieldType: 'TEXT' },
  { fieldName: 'isCollectionpointClosed', fieldTitle: t('Το Σημείο Έκλεισε'), fieldType: 'TEXT' },
  { fieldName: 'containerReplaced', fieldTitle: t('Αντικατάσταση Κάλαθου'), fieldType: 'TEXT' },
  { fieldName: 'driverComments', fieldTitle: t('Σχόλια Οδηγού'), fieldType: 'TEXT' },
  { fieldName: 'isActive', fieldTitle: t('Ενεργό'), fieldType: 'TEXT' },
  { fieldName: 'firstRec', fieldTitle: t('Δήλωση'), fieldType: 'TEXT' },
  { fieldName: 'lastRec', fieldTitle: t('Αλλαγή'), fieldType: 'TEXT' },
];

export async function handleExport(client, queryState: any, t): Promise<void> {
  // this is the function expected by exportXlsModal
  function useGetQuery() {
    const [get, { loading, data }] = useLazyQuery(gqlGetQuery, {
      onCompleted: transformData,
    });
    const [result, setResult] = useState<any>([get, { loading, data: undefined }]);

    function transformData(data) {
      const recs = data.getContainerQrcodes.data.map((values) => ({
        ...values,
        googlemapurl:
          values.latitude && values.longitude
            ? `https://www.google.com/maps/search/?api=1&query=${values.latitude},${values.longitude}`
            : '-',
        cp_googlemapurl:
          values.collectionpoint.latitude && values.collectionpoint.longitude
            ? `https://www.google.com/maps/search/?api=1&query=${values.collectionpoint.latitude},${values.collectionpoint.longitude}`
            : '-',
        distance:
          values.latitude &&
          values.longitude &&
          values.collectionpoint.latitude &&
          values.collectionpoint.longitude
            ? Math.round(
                calculateDistance(
                  values.latitude,
                  values.longitude,
                  values.collectionpoint.latitude,
                  values.collectionpoint.longitude,
                ),
              )
            : '-',
        isNewCollectionpoint: !values.isNewCollectionpoint ? t('ΟΧΙ') : t('ΝΑΙ'),
        isCollectionpointClosed: !values.isCollectionpointClosed ? t('ΟΧΙ') : t('ΝΑΙ'),
        containerReplaced: !values.containerReplaced ? t('ΟΧΙ') : t('ΝΑΙ'),
        isActive: !values.isActive ? t('ΟΧΙ') : t('ΝΑΙ'),
        firstRec: `${
          values.firstDate ? format(parseISO(values.firstDate), 'dd/MM/yy HH:mm') : ''
        } - ${values.firstUser}`,
        lastRec: `${values.lastDate ? format(parseISO(values.lastDate), 'dd/MM/yy HH:mm') : ''} - ${
          values.lastUser
        }`,
      }));
      const trsData = {
        getContainerQrcodes: {
          data: recs,
          recordCount: data?.getContainerQrcodes?.recordCount,
        },
      };
      setResult([get, { loading, data: trsData }]);
    }
    // return [get, { loading, data }];
    return result;
  }

  const filterVariables = {
    where: buildWhereFromFilterValues(queryState?.filterValues),
  };

  await exportXlsModal({
    apolloClient: client,
    exportFileName: 'export_QrCodes',
    getLazyQuery: useGetQuery,
    columnsDef: exportColumns(t),
    filterVariables,
  });
}
